/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, ReactElement } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { CHANNELS } from '@/app.constant'
import { getClasses } from '@/store/dashboard/actions'
import { getDateFilters } from '@/utils/filtersUtil'
import AgentsService from '@/services/AgentsService'
import CustomSelect from '@/components/UI/Select'
import DateSelect from '@/components/shared/DateSelect'

interface Props {
  hasFilterUpdated?: (e: any) => void
  filtersData?: {
    filter_from_date?: string
    filter_to_date?: string
    filter_channel?: string
  }
  search?: any
  hideChannels?: boolean | undefined
  hideDateOptions?: boolean | undefined
  options?: any[]
  placeholder?: string
  defaultStatus?: { value: string | null; label: string }
  hideClasses?: boolean
  showFilter?: boolean
  setSelectedClass?: (e: any) => void
  isClassMultiSelect?: boolean
  isOrganisationDashboard?: boolean
}

interface Class {
  connectionIds: string
  connections: Array<{ connection_id: string }>
  label: string
  name: string
  value: string
  _id: string
}

interface Agent {
  label: string
  name: string
  value: string
  _id: string
}

const agentsService = new AgentsService()

function NewDashboardFilters({
  isClassMultiSelect = true,
  isOrganisationDashboard,
  ...props
}: Props): ReactElement {
  const { t } = useTranslation('translation')
  const dispatch = useDispatch()
  const [classes, setClasses] = useState<Class[]>([])
  const [selectedChannel, setChannels] = useState(CHANNELS[0])
  const [connectionIds, setConnectionIds] = useState<Class[]>([])
  const [selectedAgents, setSelectedAgents] = useState<Agent[]>([])

  useEffect(() => {
    setConnectionIds([])
    if (typeof props?.setSelectedClass === 'function') {
      props?.setSelectedClass([])
    }
  }, [isClassMultiSelect])

  const [defaultConnectionIds, setDefaultConnectionIds] = useState(null)
  const [loading, setLoading] = useState(true)
  const [filtersData, setFiltersData] = useState({
    filter_from_date: null,
    filter_to_date: null,
    filter_channel: null,
    filter_connection_ids: null,
    filter_agent_ids: null,
    dateParameters: { value: '30', label: t('Last 30 days') },
  })

  const [dateFilters, setDateFilters] = useState({
    fromDate: null,
    toDate: null,
    dateParameters: {
      value: '30',
      label: t('Last 30 days'),
    },
  })

  const { data: agents } = useQuery({
    queryKey: ['all-agents'],
    enabled: isOrganisationDashboard,
    queryFn: async () => {
      const response = await agentsService.getAllAgents()

      return response.data.data.agents.map(agent => ({
        label: agent.name,
        name: agent.name,
        value: agent._id,
        _id: agent._id,
      }))
    },
  })

  useEffect(() => {
    const query = new URLSearchParams(props.search)
    const { filter_from_date, filter_to_date } = getDateFilters(
      query,
      filtersData,
    )
    setDateFilters({
      ...dateFilters,
      fromDate: filter_from_date,
      toDate: filter_to_date,
    })

    const fn = async () => {
      const { classes }: any = await dispatch(getClasses())
      setClasses(classes)

      const hasClassFilter = query.get('class')
      if (hasClassFilter) {
        const queryClasses = hasClassFilter.split(',')
        const defaultConnectionIds: any = []
        const connectionIds: any = []
        await queryClasses.forEach((className: any) => {
          const filteredClass = classes.find(
            (c: { ḷabel: string; value: string; connectionIds: string }) =>
              c.value === className,
          )
          if (filteredClass) {
            connectionIds.push(filteredClass)
            defaultConnectionIds.push(filteredClass.connectionIds)
          }
        })
        setConnectionIds(connectionIds)
      } else {
        const connectionIds = classes
          .map(
            (c: { ḷabel: string; value: string; connectionIds: string }) =>
              c.connectionIds,
          )
          .join(',')
        setDefaultConnectionIds(connectionIds)
      }
      setLoading(false)
    }
    setLoading(true)
    fn()
  }, [])

  useEffect(() => {
    if (loading) {
      return
    }

    const filter = {
      filter_from_date: dateFilters.fromDate,
      filter_to_date: dateFilters.toDate,
      filter_channel:
        selectedChannel && selectedChannel.value ? selectedChannel.value : null,
      filter_connection_ids:
        connectionIds && connectionIds.length > 0
          ? connectionIds.map((c: any) => c.connectionIds).join(',')
          : defaultConnectionIds
            ? null // defaultConnectionIds
            : null,
      filter_agent_ids:
        selectedAgents && selectedAgents.length > 0
          ? selectedAgents.map((a: any) => a._id).join(',')
          : null,
      dateParameters: filtersData.dateParameters,
    }

    setFiltersData({
      ...filtersData,
      ...filter,
    })
    props.hasFilterUpdated({ ...filtersData, ...filter })
  }, [
    selectedChannel,
    connectionIds,
    defaultConnectionIds,
    loading,
    dateFilters,
    selectedAgents,
  ])

  return (
    <div className="tw-py-2 tw-sticky tw-top-0 tw-bg-white d-flex flex-wrap tw-justify-between tw-items-center tw-w-full tw-z-50">
      {!props.hideClasses && (
        <CustomSelect
          multiple
          showChip
          maxChips={2}
          value={connectionIds.map(c => c.value)}
          options={classes}
          placeholder={t('All Classes')}
          onChange={(_value, options) => {
            setConnectionIds(options as Class[])
            if (typeof props?.setSelectedClass === 'function') {
              props?.setSelectedClass(options)
            }
          }}
          slotProps={{ root: { sx: { width: '240px' } } }}
        />
      )}

      {props?.showFilter === false ? (
        <div className="h-26">
          <div className="tw-h-9 select-sort  shadow-none radius-8"></div>
        </div>
      ) : (
        <div className="tw-flex tw-gap-2">
          {!props.hideChannels && (
            <CustomSelect
              placeholder={t('All Channels')}
              options={CHANNELS}
              value={selectedChannel.value}
              onChange={(_value, option) => {
                setChannels(option)
              }}
              slotProps={{ root: { sx: { width: '200px' } } }}
            />
          )}

          {isOrganisationDashboard ? (
            <CustomSelect
              multiple
              showChip
              maxChips={2}
              value={selectedAgents.map(c => c.value)}
              options={agents || []}
              placeholder={t('All Agents')}
              onChange={(_value, options) => {
                setSelectedAgents(options as Agent[])
                if (typeof props?.setSelectedClass === 'function') {
                  props?.setSelectedClass(options)
                }
              }}
              slotProps={{ root: { sx: { width: '240px' } } }}
            />
          ) : null}

          {!props.hideDateOptions && (
            <DateSelect
              onChange={({ fromDate, toDate, dateParameters }) => {
                setDateFilters({ fromDate, toDate, dateParameters })
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default NewDashboardFilters
